import { DeleteOutline, DownloadOutlined } from '@mui/icons-material'
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { t } from 'i18next'
import { useCallback, useMemo } from 'react'
import { AuthorizationType } from 'src/enums/auth'
import { IFormSchema, IFormSchemaDto, IFormSchemaReport } from 'src/generated-types/form-schema'
import { ITaskFormSchema } from 'src/generated-types/taskFormSchema'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { Flex } from 'src/styles/flexComponent'
import { Padding } from 'src/styles/paddingComponent'
import { GridActionButton } from '../common/GridActionButton'
import { Spinner } from '../Spinner'
import GenericTable from '../table/GenericTable'
import ReportViewer from './ReportViewer'
import { EmptyStatusCard } from '../mooring/EmptyStatusCard'
import { IEmptyStatusProps } from 'src/generated-types/empty-status'
import NoReports from '../../assets/svg/no-reports.svg'

interface IOverallReportTabContainer extends IEmptyStatusProps {
  onComplete: (row: any, reportId?: number) => void
  disabled?: boolean
  bulkUploadLoading?: number
  createPermissions: number[]
  context?: React.Context<any> | null
  handleExport: (row: any) => void
  data: IFormSchemaDto[] | undefined
  requiredReports?: ITaskFormSchema[]
  deletePermissions: number[]
  handleDelete: (id: number) => void
  isLoading: boolean
  height?: number | string
  rowsToFit?: { min?: number; max?: number; fill?: boolean }
  isScheduledTaskIgnored?: boolean
  minimalEmptyStatus?: boolean
}

const OverallReportTabContainer: React.FC<IOverallReportTabContainer> = ({
  deletePermissions,
  handleDelete,
  isLoading,
  height,
  rowsToFit,
  data,
  requiredReports,
  onComplete,
  disabled,
  handleExport,
  bulkUploadLoading,
  context,
  createPermissions,
  isScheduledTaskIgnored = false,
  emptyStatusImageWidth,
  emptyStatusContent,
  emptyStatusTitle,
  emptyStatusTitleFontSize,
  emptyStatusContentFontSize,
  imageContainerClasses,
  minimalEmptyStatus = false,
}) => {
  const { showDialog } = useDialogCtx()

  const STATIC_COLUMNS: GridColumns = [
    { field: 'name', headerName: t('name', { ns: 'common' }), flex: 1 },
    {
      field: 'description',
      headerName: t('description', { ns: 'common' }),
      flex: 1,
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      renderCell: ({ row }) => {
        return row?.formSchemaReports == undefined ? (
          <Padding.p8>
            <Button variant="outlined" sx={{ height: '30px' }} onClick={() => onComplete(row)} disabled={disabled}>
              {t('complete')}
            </Button>
          </Padding.p8>
        ) : (
          <Flex.Row justifyContent={'flex-end'} width={'100px'}>
            {bulkUploadLoading == row.id ? (
              <Spinner size={25} />
            ) : (
              <GridActionButton
                onClick={async () => {
                  handleExport(row)
                }}
                disabled={disabled}
                context={context}
                permissionBinaryValues={createPermissions}
                authorizationType={AuthorizationType.Disabled}
                buttonIcon={<DownloadOutlined sx={{ fontSize: '20px' }} />}
                height="32px"
                width="32px"
              />
            )}
          </Flex.Row>
        )
      },
    },
  ]

  const rows = useMemo(() => {
    const returnArray = data?.filter(s => s?.formSchemaReports != null && s?.formSchemaReports?.length > 0) || []
    const requiredArray = requiredReports
      ?.map(d => ({ ...d.formSchema, id: d.formSchemaId } as IFormSchema))
      .filter(ra => !returnArray.find(r => r.name == ra.name))
    return requiredArray ? requiredArray?.concat(returnArray as IFormSchema[]) : returnArray
  }, [data])

  const renderExpandedRow = useCallback(
    ({ row: schema }) => {
      return schema?.formSchemaReports != undefined && schema?.formSchemaReports?.length > 0 ? (
        <Box marginLeft={2} marginTop={1} paddingBottom={2}>
          {schema?.formSchemaReports?.map((report: IFormSchemaReport) => {
            const isDraft = report?.isDraft
            return (
              <Stack
                key={report.id}
                direction={'row'}
                paddingX={'20px'}
                paddingY={'5px'}
                style={{ background: 'white', cursor: 'pointer' }}
                mr={1}
                minWidth={0}
              >
                <Box
                  width={'100%'}
                  key={report?.id}
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  marginTop={1}
                  onClick={() => {
                    if (isDraft && !isScheduledTaskIgnored) {
                      onComplete(schema, report?.id)
                      return
                    }
                    showDialog(
                      ReportViewer,
                      {
                        componentProps: {
                          layouts: JSON.parse(report?.formSchemaVersion?.layout || ''),
                          schema: schema,
                          report: report,
                          disabled: disabled,
                          createdAt: report?.createdAt,
                          createdByUser: report?.createdByUser,
                        },
                        opts: {
                          maxWidth: 'lg',
                          fullWidth: true,
                        },
                      },
                      undefined,
                      true,
                    )
                  }}
                >
                  <Box flex={1} display="flex" alignItems="center" gap={1}>
                    <Typography>{`${report?.createdByUser?.firstName} ${report?.createdByUser?.lastName}`}</Typography>
                    {isDraft && !isScheduledTaskIgnored && (
                      <Tooltip
                        title={(report?.draftCaption || t('draft', { ns: 'form-schema-report' })) as string}
                        arrow
                        placement="bottom"
                      >
                        <Chip
                          label={t('draft', { ns: 'form-schema-report' })}
                          size="small"
                          variant="outlined"
                          sx={{ cursor: 'default' }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Box flex={1}>
                    <Typography>{`${new Date(report?.createdAt + 'Z' || '').toLocaleString()}`}</Typography>
                  </Box>
                </Box>
                <GridActionButton
                  onClick={async () => {
                    handleDelete(report.id as number)
                  }}
                  disabled={disabled}
                  context={context}
                  permissionBinaryValues={deletePermissions}
                  authorizationType={AuthorizationType.Disabled}
                  buttonIcon={<DeleteOutline sx={{ fontSize: '20px' }} />}
                  height="32px"
                  width="32px"
                />
              </Stack>
            )
          })}
        </Box>
      ) : null
    },
    [disabled, handleDelete, onComplete],
  )

  return (
    <GenericTable
      rowsToFit={rowsToFit}
      id={'overall-report-table'}
      columns={STATIC_COLUMNS}
      data={rows}
      loading={isLoading}
      autoHeight={false}
      getDetailPanelHeight={() => 'auto'}
      height={height}
      getDetailPanelContent={params => renderExpandedRow(params)}
      getRowHeight={() => 40}
      getRowClassName={params => (params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd')}
      noDataComponent={() => (
        <Box height="100%">
          <EmptyStatusCard
            imageWidth={emptyStatusImageWidth ?? '40px'}
            image={NoReports}
            header={emptyStatusTitle}
            content={emptyStatusContent}
            headerFontSize={emptyStatusTitleFontSize}
            contentFontSize={emptyStatusContentFontSize}
            contentClasses="mt-2"
            contentTopMargin="10px"
            imageContainerClasses={imageContainerClasses ?? 'pb-4 pt-4'}
            height="100%"
            hideImage={minimalEmptyStatus}
            compact={minimalEmptyStatus}
          />
        </Box>
      )}
    />
  )
}

export default OverallReportTabContainer
